/**
 * reCAPTCHA v3 — carregador do script + geração de token.
 *
 * O `siteKey` vem do backend (`getCardPublicKey().recaptcha_site_key`); quando
 * null, o recurso está desligado e o CardStep nem chama isto. Defesa em
 * profundidade no checkout de cartão (o PagBank orienta) — o backend valida o
 * token com o Google e pontua (score v3).
 *
 * Best-effort no front: single-flight + timeout de 8s. Se falhar (adblocker,
 * rede), quem chama trata como "sem token" e deixa o backend decidir — não
 * trava a tela aqui (§10: falha de SDK vira estado tratável).
 */

interface GreCaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GreCaptcha;
  }
}

const LOAD_TIMEOUT_MS = 8000;

let loadPromise: Promise<void> | null = null;
let loadedKey: string | null = null;

function loadScript(siteKey: string): Promise<void> {
  if (loadPromise && loadedKey === siteKey) return loadPromise;
  loadedKey = siteKey;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    const timer = setTimeout(() => reject(new Error('recaptcha_load_timeout')), LOAD_TIMEOUT_MS);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      loadPromise = null; // permite retry numa próxima tentativa
      reject(new Error('recaptcha_load_failed'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** Gera um token v3 pra `action`. Lança se o script/API não vier — o chamador
 *  decide (aqui a política é seguir sem token e deixar o backend julgar). */
export async function executeRecaptcha(siteKey: string, action: string): Promise<string> {
  await loadScript(siteKey);
  const g = window.grecaptcha;
  if (!g) throw new Error('recaptcha_unavailable');
  await new Promise<void>((resolve) => g.ready(resolve));
  return g.execute(siteKey, { action });
}
